// @ts-nocheck
'use client'

import { useState, useEffect, useRef } from 'react'
import { PlusIcon, DocumentArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import { getFormattedMissingFieldsMessage } from 'utils'
import SelectComponent from 'components/common/SelectComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import BlockTable from 'components/common/BlockTable'
import BlockFormModal from 'components/modals/BlockFormModal'
import TunnelRowModal from 'components/modals/TunnelRowModal'
import ActionCardComponent from 'components/common/ActionCardComponent'
import UploadFarmingBlockModal from 'components/modals/UploadFarmingBlockModal'
import PageLoadingState from 'components/common/PageLoadingState'
import ModalComponent from 'components/common/ModalComponent'

import { useAuth } from 'auth/AuthContext'

const FarmingBlocksManagement = ({ blockGroups, onRefreshBlockGroups }) => {
  const tunnelRefreshRef = useRef(null)
  const { user } = useAuth()
  const { t } = useTranslation()
  const [state, setState] = useState({
    blocks: [],
    error: null,
    selectedBlock: {},
    selectedBlockGroupId: '',
    filter: '',
    isOpenBlockFormModal: false,
    searchTerm: '',
    isTunnelRowModalOpen: false,
    selectedTunnelRowIndex: null,
    selectedTunnelRowData: {
      rowId: '',
      cropId: '',
      varietyId: '',
      numberOfPlants: '',
      subA: null,
      subB: null
    },
    varieties: [],
    isOpenUploadFarmingBlockModal: false,
    loading: true,
    showDeleteConfirm: false,
    pendingDelete: null
  })

  // Load farming blocks
  const getFarmingBlocks = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/farming-blocks/${user.farmId}?filter=${state.filter}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })

      setState(s => ({
        ...s,
        blocks: Array.isArray(response.data) ? response.data : [],
        error: null,
        loading: false
      }))
    } catch (err) {
      showAlert({ message: t('mgmt.farmingBlocks.errLoad'), icon: 'error' })
      setState(s => ({
        ...s,
        error: t('mgmt.farmingBlocks.errLoad'),
        loading: false
      }))
    }
  }

  const handleDelete = (blockId) => {
    const block = state.blocks.find(b => b.farmingBlockId === blockId) || { farmingBlockId: blockId, name: t('mgmt.farmingBlocks.thisBlock') }
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: block }))
  }

  const confirmDelete = async () => {
    const blockId = state.pendingDelete.farmingBlockId
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/farming-blocks/${blockId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'application/json'
        },
        alertResult: true
      })
      await getFarmingBlocks()
    } catch (err) {
      showAlert({ message: t('mgmt.farmingBlocks.errDelete'), icon: 'error' })
      setState(s => ({
        ...s,
        error: t('mgmt.farmingBlocks.errDelete')
      }))
    }
  }

  const putPostFarmingBlocks = async formData => {
    const requiredFields = ['name', 'cropId', 'varietyId', 'area']
    const htmlMessage = getFormattedMissingFieldsMessage(formData, requiredFields)
    if (htmlMessage) {
      showAlert({ html: htmlMessage, icon: 'error' })
      return
    }

    const isEdit = !!state.selectedBlock?.farmingBlockId
    const url = isEdit
      ? `/management/farming-blocks/${user.farmId}/update/${state.selectedBlock.farmingBlockId}`
      : `/management/farming-blocks/${user.farmId}`
    const method = isEdit ? 'PUT' : 'POST'

    // keep blockGroupId ONLY if it's a finite number (coerces strings)
    const { blockGroupId, ...rest } = formData
    const num = Number(blockGroupId)
    const data = Number.isFinite(num) ? { ...rest, blockGroupId: num } : rest

    try {
      await makeRequest({
        method,
        url,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'application/json'
        },
        data,
        alertResult: true
      })

      getFarmingBlocks()
      setState(s => ({ ...s, selectedBlock: {} }))
      onCloseBlockFormModal()
    } catch (err) {
      showAlert({ message: t('mgmt.farmingBlocks.errSave'), icon: 'error' })
      setState(s => ({ ...s, error: t('mgmt.farmingBlocks.errSave'), selectedBlock: {} }))
    }
  }

  const updateFarmingBlockGrid = async (block, blockConfig) => {
    const blockGroupId = Number(block.blockGroupId)
    const payload = {
      name: block.name,
      area: block.area,
      cropId: Number(block.cropId),
      varietyId: Number(block.varietyId),
      useSubBlockBarcode: !!block.useSubBlockBarcode,
      isHydroponicBlock: !!block.isHydroponicBlock,
      accessTunnel: Number(block.accessTunnel || 0),
      accessWindow: Number(block.accessWindow || 1),
      blockConfig: (blockConfig || []).map(config => ({
        numberOfTunnels: Number(config.numberOfTunnels || 0),
        numberOfWindows: Number(config.numberOfWindows || 0)
      })),
      ...(Number.isFinite(blockGroupId) ? { blockGroupId } : {})
    }

    try {
      await makeRequest({
        method: 'PUT',
        url: `/management/farming-blocks/${user.farmId}/update/${block.farmingBlockId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'application/json'
        },
        data: payload,
        alertResult: true
      })

      setState(s => ({
        ...s,
        blocks: s.blocks.map(currentBlock => (
          currentBlock.farmingBlockId === block.farmingBlockId
            ? { ...currentBlock, blockConfig: payload.blockConfig }
            : currentBlock
        ))
      }))
      getFarmingBlocks()
    } catch (err) {
      showAlert({ message: t('mgmt.farmingBlocks.errUpdateGrid'), icon: 'error' })
      setState(s => ({ ...s, error: t('mgmt.farmingBlocks.errUpdateGrid') }))
    }
  }

  const getFarmingBlocksVarieties = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: '/management/farming-blocks/varieties',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })
      setState(s => ({
        ...s,
        varieties: Array.isArray(response.data) ? response.data : []
      }))
    } catch (err) {
      showAlert({ message: t('mgmt.farmingBlocks.errLoadVarieties'), icon: 'error' })
      setState(s => ({
        ...s,
        error: t('mgmt.farmingBlocks.errLoadVarieties'),
        varieties: []
      }))
    }
  }

  const onOpenBlockFormModal = () => {
    setState(s => ({
      ...s,
      selectedBlock: {},
      isOpenBlockFormModal: true
    }))
  }

  const onEditBlock = (block) => {
    setState(s => ({
      ...s,
      selectedBlock: _.cloneDeep(block),
      isOpenBlockFormModal: true
    }))
  }

  const onCloseBlockFormModal = () => {
    setState(s => ({
      ...s,
      selectedBlock: {},
      isOpenBlockFormModal: false
    }))
  }

  const onFilterBlockGroupsList = (e) => {
    setState(s => ({
      ...s,
      selectedBlockGroupId: e.target.value
    }))
  }

  const toggleIsTunnelRowModalOpen = (index = null, initialData = {}) => {
    setState(s => ({
      ...s,
      isTunnelRowModalOpen: !s.isTunnelRowModalOpen,
      selectedTunnelRowIndex: index !== null ? index : s.selectedTunnelRowIndex,
      selectedTunnelRowData: initialData
    }))
  }

  const onToggleUploadCsvModal = () => {
    setState(s => ({
      ...s,
      isOpenUploadFarmingBlockModal: !s.isOpenUploadFarmingBlockModal
    }))
  }

  const filteredBlocks = state.blocks.filter(block => {
    // Filter by block group if selected
    const matchesGroup = !state.selectedBlockGroupId ||
      block.blockGroupId === parseInt(state.selectedBlockGroupId)

    // Filter by search term if provided
    const matchesSearch = !state.searchTerm ||
      block.name.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      (block.cropId && String(block.cropId).toLowerCase().includes(state.searchTerm.toLowerCase())) ||
      (block.varietyId && String(block.varietyId).toLowerCase().includes(state.searchTerm.toLowerCase()))

    return matchesGroup && matchesSearch
  })

  useEffect(() => {
    if (!user?.farmId) return
    getFarmingBlocks()
    getFarmingBlocksVarieties()
  }, [user?.farmId])

  useEffect(() => {
    if (blockGroups.length > 0) {
      // Refresh blocks when block groups change (e.g., after creating/deleting a group)
      getFarmingBlocks()
    }
  }, [blockGroups])

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.farmingBlocks.loading')} rows={8} className='mb-6' />
  }

  return (
    <>
      {!!user.farmId && (
        <ActionCardComponent>
          <input
            type='text'
            value={state.searchTerm}
            placeholder={t('mgmt.farmingBlocks.filter')}
            onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
            className='search-input'
            data-align='left'
          />

          <SelectComponent
            data-align='left'
            id='blockGroupId'
            name='blockGroupId'
            value={state.selectedBlockGroupId}
            onChange={onFilterBlockGroupsList}
          >
            <option value=''>{t('mgmt.farmingBlocks.allGroups')}</option>
            {blockGroups.map(group => (
              <option key={group.blockGroupId} value={group.blockGroupId}>
                {group.groupName}
              </option>
            ))}
          </SelectComponent>

          <ButtonComponent onClick={() => setState(s => ({ ...s, isOpenUploadFarmingBlockModal: true }))}>
            <DocumentArrowUpIcon className='w-5 h-5' />
            <span className='font-medium'>{t('mgmt.farmingBlocks.import')}</span>
          </ButtonComponent>

          <ButtonComponent onClick={onOpenBlockFormModal}>
            <PlusIcon className='w-5 h-5' />
            <span className='font-medium'>{t('mgmt.farmingBlocks.new')}</span>
          </ButtonComponent>
        </ActionCardComponent>
      )}

      {/* farming blocks table */}
      {state.blocks.length !== 0 &&
        <BlockTable
          onEdit={onEditBlock}
          onDelete={handleDelete}
          onUpdateBlockConfig={updateFarmingBlockGrid}
          blockGroups={blockGroups}
          filteredBlocks={filteredBlocks}
          toggleIsTunnelRowModalOpen={toggleIsTunnelRowModalOpen}
          tunnelRefreshRef={tunnelRefreshRef}
        />}

      {/* block form modal */}
      <BlockFormModal
        isOpen={state.isOpenBlockFormModal}
        selectedBlock={state.selectedBlock}
        blockGroups={blockGroups}
        varietiesOptions={state.varieties}
        onClose={onCloseBlockFormModal}
        onSave={putPostFarmingBlocks}
      />

      <TunnelRowModal
        isOpen={state.isTunnelRowModalOpen}
        rowIndex={state.selectedTunnelRowIndex}
        onClose={toggleIsTunnelRowModalOpen}
        initialData={state.selectedTunnelRowData}
        varietiesOptions={state.varieties}
        onRefresh={() => {
          if (tunnelRefreshRef.current) {
            tunnelRefreshRef.current()
          }
        }}
      />
      <UploadFarmingBlockModal
        isOpen={state.isOpenUploadFarmingBlockModal}
        onClose={onToggleUploadCsvModal}
      />

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.farmingBlocks.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {t('mgmt.farmingBlocks.removeConfirm', { name: state.pendingDelete?.name ?? '' })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDelete}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  )
}

export default FarmingBlocksManagement
